// @owners { team: patients }
import { Toast, ToastContext } from '@alto/design-system';
import { CartEndpoint } from '@alto/scriptdash/alto/patient_app/carts/v1/cart_endpoint';
import { type PriceType } from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useState } from 'react';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { apiEndpointHandler } from '~shared/helpers/api';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getOrigin from '~shared/lib/analytics/src/getOrigin';
import { queries } from '~shared/queries/query-keys';

const cartEndpoint = CartEndpoint(apiEndpointHandler);

type UpdateCartItemParams = {
  resource_id: number;
  resource_type: string;
  quantity?: number;
  auto_refill_enabled?: boolean | null;
  selected_price_type?: PriceType | null;
};

type UpdateCartItemRequestParams = {
  quantity?: number;
  auto_refill_enabled?: boolean | null;
  selected_price_type?: PriceType | null;
};

type UpdateCartItemResult = {
  success: boolean;
};

type UseUpdateCartItem = {
  isPending: boolean;
  isSuccess: boolean;
  handleUpdateCartItem: (params: UpdateCartItemParams) => Promise<UpdateCartItemResult>;
};

/**
 * Hook used to update item from the patient's cart.
 *
 *
 * const result = await handleUpdateCartItem({ resource_id: rx.id, resource_type: 'Prescription' });
 *
 * // perform logic after the item is successfully updated in the cart
 * if (result.success) {
 *   ...
 * }
 */

export const useUpdateCartItem = (): UseUpdateCartItem => {
  const queryClient = useQueryClient();
  const [isSuccess, setIsSuccess] = useState(false);
  const { addToast } = useContext(ToastContext);
  const { trackEvent } = useAnalytics();

  const cartItemUpdatedEvent = useCallback(
    ({
      resource_id,
      auto_refill_enabled,
      selected_price_type,
    }: {
      resource_id: number;
      auto_refill_enabled: boolean | null | undefined;
      selected_price_type: PriceType | null | undefined;
    }) => {
      trackEvent({
        event: EVENTS.CART__ITEM_UPDATED,
        params: {
          origin: getOrigin(),
          auto_refill_status: auto_refill_enabled ? 'auto refill' : 'one-time fill',
          price_type: selected_price_type,
          server_side_cart: true,
        },
        additionalFields: {
          prescriptionId: resource_id,
        },
      });
    },
    [trackEvent],
  );

  const cartItemUpdatedFailedEvent = useCallback(
    ({
      resource_id,
      auto_refill_enabled,
      selected_price_type,
      error,
    }: {
      resource_id: number;
      auto_refill_enabled: boolean | null | undefined;
      selected_price_type: PriceType | null | undefined;
      error: string;
    }) => {
      trackEvent({
        event: EVENTS.CART__ITEM_UPDATED_FAILED,
        params: {
          origin: getOrigin(),
          auto_refill_status: auto_refill_enabled ? 'auto refill' : 'one-time fill',
          price_type: selected_price_type,
          server_side_cart: true,
          error,
        },
        additionalFields: {
          prescriptionId: resource_id,
        },
      });
    },
    [trackEvent],
  );

  const { isPending, mutateAsync } = useMutation({
    // this should be of type CartEndpointUpdateItemRequest once we update cart proto
    mutationFn: async (params: any) => {
      setIsSuccess(false);
      const response = await cartEndpoint.updateItem(params);
      return { response };
    },
  });

  const { cartItems } = useCartData();
  const updateCartItemApi = useCallback(
    async ({ resource_id, quantity, auto_refill_enabled, selected_price_type }: UpdateCartItemParams) => {
      // this should be of type CartEndpointUpdateParams once we update cart proto
      const requestParams: UpdateCartItemRequestParams = {
        quantity: quantity || 1,
        auto_refill_enabled,
        selected_price_type,
      };

      try {
        const cartItem = cartItems.find(
          (item) => item.resource_type === 'Prescription' && item.resource_id === resource_id,
        );

        if (!cartItem?.id) {
          throw new Error('Unable to update item in cart. Please try again.');
        }
        const { response } = await mutateAsync({ id: cartItem.id, params: requestParams });
        if (response.errors) {
          const errorMessage = getErrorMessageFromResponse({ errors: response.errors });
          addToast(<Toast variant="error">{errorMessage}</Toast>);
          cartItemUpdatedFailedEvent({
            resource_id,
            auto_refill_enabled,
            selected_price_type,
            error: 'api error',
          });
          return { success: false };
        }

        queryClient.invalidateQueries({ queryKey: queries.cart.active({}).queryKey });
        cartItemUpdatedEvent({ resource_id, auto_refill_enabled, selected_price_type });
        return { success: true };
      } catch (e) {
        console.warn(e);
        // refetch to reconcile any stale cart item IDs (e.g. item deleted server-side)
        queryClient.invalidateQueries({ queryKey: queries.cart.active({}).queryKey });
        const errorMessage = getErrorMessageFromResponse({});
        addToast(<Toast variant="error">{errorMessage}</Toast>);
        cartItemUpdatedFailedEvent({
          resource_id,
          auto_refill_enabled,
          selected_price_type,
          error: 'other error',
        });
        return { success: false };
      }
    },
    [addToast, cartItemUpdatedEvent, cartItemUpdatedFailedEvent, mutateAsync, queryClient, cartItems],
  );

  return {
    isSuccess,
    isPending,
    handleUpdateCartItem: updateCartItemApi,
  };
};
